import React, { useState, useCallback } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { Breadcrumb, Card, Col, Pagination, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import releaseNotesService from '@services/release_notes.service';
import BreadcrumbsPortal from '@rd-web-markets/shared/dist/util/BreadcrumbsPortal';
import { Table } from '@rd-web-markets/shared/dist/util';
import { AddButton, DeleteButton, EditButton } from '@rd-web-markets/shared/dist/util/buttons';
import { ChevronDownButton } from '@rd-web-markets/shared/dist/util/buttons/ChevronDownButton';
import { useErrorHandling } from '@rd-web-markets/shared/dist/hooks/useErrorHandling';
import MasterSidebar from '@rd-web-markets/shared/dist/util/MasterSidebar';
import ReleasesModal from './ReleasesModal';
import { APP_RELEASE_VERSION } from '@rd-web-markets/market/dist/constants';
import * as QueryString from 'query-string';
import SearchBar from '@components/util/SearchBar';

const initialParams = 'page=1&query=';

export default function ReleasesPage({ accountType }) {
  const history = useHistory();
  const { t } = useTranslation();
  const [modalMode, setModalMode] = useState('create');
  const [newRelease, setNewRelease] = useState({ id: null, release_number: '', release_date: null });
  const [modalShow, setModalShow] = useState(false);
  const [params, setParams] = useState(QueryString.parse(window.location.search || initialParams));

  const [releases, setReleases, totalPages, loading, refreshReleases] = releaseNotesService.useReleases(params);

  const goToNotePage = (release) => {
    history.push(`/${accountType}/release_notes/${release.id}`);
  };

  const addRelease = () => {
    setModalMode('create');
    setNewRelease({ id: null, release_number: '', release_date: new Date() });
    setModalShow(true);
  };

  const changeRelease = async (release) => {
    setModalMode('update');
    setNewRelease(release);
    setModalShow(true);
  };

  const createRelease = useCallback(async (modalMode) => {
    if (modalMode === 'create') {
      await releaseNotesService.create(newRelease);
    } else {
      await releaseNotesService.update(newRelease.id, newRelease);
    }
    setModalShow(false);
    await refreshReleases();
  }, [newRelease, refreshReleases]);

  const deleteRelease = useErrorHandling(useCallback(async (release) => {
    if (window.confirm('Are you sure you want to delete this release?')) {
      await releaseNotesService.delete(release.id);
      await refreshReleases();
    }
  }, [refreshReleases]));

  const search = (query) => {
    setParams({ ...params, query, page: 1 });
  };

  const searchReset = () => {
    setParams({ ...params, query: '', page: 1 });
  };

  const handlePage = (pageNumber) => {
    setParams({ ...params, page: pageNumber });
  };

  const headers = [
    { text: t('release_number') },
    { text: t('release_date') },
  ];
  if (accountType === 'admin') headers.push({ text: t('actions') });

  const rows = releases.map((release) => ({
    key: release.id,
    columns: [
      release.release_number,
      release.release_date,
    ],
    controls: accountType === 'admin' ? <>
      <ChevronDownButton onClick={() => goToNotePage(release)} />
      <EditButton onClick={() => changeRelease(release)} />
      <DeleteButton onClick={() => deleteRelease(release)} />
    </> : <>
      <ChevronDownButton onClick={() => goToNotePage(release)} />
    </>
  }));

  const paginationItems = [];
  for (let number = 1; number <= totalPages; number++) {
    paginationItems.push(
      <Pagination.Item
        key={number}
        active={number === parseInt(params.page || 1)}
        onClick={() => handlePage(number)}
      >
        {number}
      </Pagination.Item>
    );
  }

  return <>
    <ReleasesModal
      newRelease={newRelease}
      setNewRelease={setNewRelease}
      modalShow={modalShow}
      setModalShow={setModalShow}
      createRelease={createRelease}
      modalMode={modalMode}
    />
    <MasterSidebar accountType={accountType} currentPage='' />
    <BreadcrumbsPortal>
      <Breadcrumb>
        <Breadcrumb.Item linkAs={Link} linkProps={{ to: `/${accountType}` }}>{t('home')}</Breadcrumb.Item>
        <Breadcrumb.Item linkAs={Link} linkProps={{ to: `/${accountType}/release_notes` }}>{t('releases')}</Breadcrumb.Item>
      </Breadcrumb>
    </BreadcrumbsPortal>

    <Card>
      <Card.Body>
        <Row className="mb-3">
          <Col md={4}>
            {accountType === 'admin' && (
              <AddButton onClick={addRelease} text={t('add_release')} />
            )}
          </Col>
          <Col md={{ span: 4, offset: 4 }}>
            <SearchBar
              onSubmit={search}
              onSearchReset={searchReset}
              loading={loading}
              query={params.query}
              placeholder={t('search_releases')}
            />
          </Col>
        </Row>

        <Table headers={headers} rows={rows} />
      </Card.Body>
    </Card>

    {totalPages > 1 && (
      <Pagination className="justify-content-end mt-3">
        {paginationItems}
      </Pagination>
    )}

    <div className="text-muted text-center mt-3">
      {APP_RELEASE_VERSION}
    </div>
  </>;
}
